/**
 * Amplitude initialization functionality
 */ import * as amplitude from '@amplitude/analytics-browser';
import * as sessionReplay from '@amplitude/plugin-session-replay-browser';
import { getUserUuid, getVCookie, isWindowDefined } from '@chegg/utils';
import { capturePageAction, ActionSeverity } from '@chegg/observability';
import { setAmplitudeInitialized, getAmplitudeInitialized } from './amplitude-state';
import { getAmplitudeApiKey, APP_VERSION } from './utils';
import { setAmplitudeUserIdentity } from './identity';
import { createUnifiedAmplitudePlugin, resetPageViewEvent } from './plugin';
import { setupAmplitudeTokenListener } from './setup-token-listener';
// Ensure LogLevel is available for the initialization
const { LogLevel } = amplitude.Types;
/**
 * Initialize Amplitude analytics with appropriate configuration
 * This function is safe to call multiple times and can be called from different contexts
 */ export function initializeAmplitude() {
    if (getAmplitudeInitialized()) {
        console.log('Amplitude already initialized');
        return;
    }
    if (!isWindowDefined) {
        return;
    }
    try {
        const userId = getUserUuid() || undefined;
        const deviceId = getVCookie() || undefined;
        // Initialize Amplitude with the appropriate API key
        amplitude.init(getAmplitudeApiKey(), userId, {
            deviceId,
            appVersion: APP_VERSION,
            autocapture: {
                attribution: true,
                pageViews: true,
                sessions: true,
                elementInteractions: {
                    cssSelectorAllowlist: [
                        'a',
                        'button',
                        'input',
                        'select',
                        'textarea',
                        'label',
                        'video',
                        'audio',
                        '[contenteditable="true" i]',
                        '[data-amp-default-track]',
                        '.amp-default-track'
                    ],
                    dataAttributePrefix: 'data-test'
                },
                formInteractions: true,
                fileDownloads: true
            },
            logLevel: LogLevel.Warn
        });
        // Add the unified plugin that handles event enrichment
        amplitude.add(createUnifiedAmplitudePlugin());
        // Add the session replay plugin
        try {
            amplitude.add(sessionReplay.plugin({
                sampleRate: 1
            }));
        } catch (error) {
            capturePageAction({
                action: 'Error adding Session Replay plugin',
                severity: ActionSeverity.WARNING,
                location: 'amplitude-analytics'
            }, {
                error: String(error)
            });
        }
        // Set user identity after initialization
        setAmplitudeUserIdentity();
        // Set up automatic token listener for subscription status updates
        void setupAmplitudeTokenListener({
            enabled: true,
            onError: (error)=>{
                capturePageAction({
                    action: 'Token listener setup error',
                    severity: ActionSeverity.WARNING,
                    location: 'amplitude-analytics'
                }, {
                    error: error.message
                });
            }
        });
        setAmplitudeInitialized(true);
        console.debug('Amplitude initialized successfully', {
            userId,
            deviceId
        });
    } catch (error) {
        capturePageAction({
            action: 'Error during Amplitude initialization',
            severity: ActionSeverity.WARNING,
            location: 'amplitude-analytics'
        }, {
            error: String(error)
        });
    }
}
/**
 * Check if Amplitude is initialized
 */ export const isAmplitudeInitialized = ()=>getAmplitudeInitialized();
/**
 * Reset the initialization state (useful for testing)
 * @internal
 */ export function resetAmplitudeInitialization() {
    setAmplitudeInitialized(false);
    resetPageViewEvent();
}
